/**
 * CampaignQueueItemsTable - Tabela de itens da fila de campanhas
 * 
 * Exibe lista de recipients com status, expandível para detalhes
 */

import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CampaignQueueItemRow } from './CampaignQueueItemRow';
import type { CampaignQueueItem } from '@/hooks/useCampaignQueueMonitor';

interface CampaignQueueItemsTableProps {
  items: CampaignQueueItem[];
  isLoading: boolean;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
}

// Mapear status para cor do badge
const statusColors: Record<string, string> = {
  pending: 'bg-slate-500',
  queued: 'bg-blue-500',
  processing: 'bg-purple-500',
  sending: 'bg-indigo-500',
  sent: 'bg-green-500',
  delivered: 'bg-emerald-500',
  read: 'bg-teal-500',
  failed: 'bg-destructive',
  skipped: 'bg-muted-foreground'
};

// Labels amigáveis para status
const statusLabels: Record<string, string> = {
  pending: 'Pendente',
  queued: 'Na Fila',
  processing: 'Processando',
  sending: 'Enviando',
  sent: 'Enviado',
  delivered: 'Entregue',
  read: 'Lido',
  failed: 'Falhou',
  skipped: 'Ignorado'
};

export function CampaignQueueItemsTable({ 
  items, 
  isLoading,
  statusFilter,
  onStatusFilterChange
}: CampaignQueueItemsTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header com filtro de status */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Recipients ({items.length})</h3>
        <Select value={statusFilter} onValueChange={onStatusFilterChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtrar status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos Status</SelectItem>
            <SelectItem value="pending">Pendente</SelectItem>
            <SelectItem value="queued">Na Fila</SelectItem>
            <SelectItem value="processing">Processando</SelectItem>
            <SelectItem value="sending">Enviando</SelectItem>
            <SelectItem value="sent">Enviado</SelectItem>
            <SelectItem value="delivered">Entregue</SelectItem>
            <SelectItem value="read">Lido</SelectItem>
            <SelectItem value="failed">Falhou</SelectItem>
            <SelectItem value="skipped">Ignorado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabela */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]"></TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Campanha</TableHead>
              <TableHead>Tenant</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Tempo</TableHead>
              <TableHead>Criado</TableHead>
              <TableHead>Erro</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                  Nenhum recipient encontrado
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <CampaignQueueItemRow
                  key={item.id}
                  item={item}
                  isExpanded={expandedId === item.id}
                  onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  statusColors={statusColors}
                  statusLabels={statusLabels}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
